/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { FC, useMemo, useState } from "react";
import { BEM } from "@adapptio/lib-components";
import { createEmptyScope, SCHEMA_VALUE_TYPE, Type } from "@adapptio/lib-blueprint";

import { Panel, PANEL_STYLE, PanelNote } from "../../../../ui/components/Panel";
import { ActionEditorView } from "../ActionEditorView";
import { ILayoutConfig, LAYOUT_TYPE } from "../../../../ui/components/Layout/LayoutTypes";
import { Layout } from "../../../../ui/components/Layout/Layout";
import { LayoutState } from "../../../../ui/components/Layout/LayoutState";
import { useBlueprintModel } from "../../../../ui/hooks/useBlueprintModel";
import { useSimpleEvent } from "../../../../ui/hooks/useSimpleEvent";
import { FieldSection } from "../../../../ui/components/Fields/FieldSection";
import { FieldSectionContent } from "../../../../ui/components/Fields/FieldSectionContent";
import { ScopeViewer } from "../../../../ui/components/ScopeViewer/ScopeViewer";
// import { offEvent, onEvent } from "@adapptio/lib-shared";
import { Inspector } from "../../../../inspector";
import { ACTION_DELEGATE_STATE } from "@adapptio/lib-core";

export const ActionEditorTesterLayout: ILayoutConfig = {
	root: {
		type: LAYOUT_TYPE.ROW,
		id: "layout",
		children: [
			{
				type: LAYOUT_TYPE.COLUMN,
				id: "panelLeft",
				size: "300px",
				isExpandable: true,
				defaultExpanded: true,
				children: [
					{
						type: LAYOUT_TYPE.PANEL,
						id: "parameters",
						isExpandable: false,
						contentComponent: "parameters"
					}
				]
			},
			{
				type: LAYOUT_TYPE.SPLITTER,
				id: "leftSplitter"
			},
			{
				type: LAYOUT_TYPE.COLUMN,
				id: "main",
				children: [
					{
						type: LAYOUT_TYPE.PANEL,
						id: "output",
						isExpandable: false,
						contentComponent: "output"
					}
				]
			}
		]
	}
};

export interface IActionEditorTesterContentProps {
	view: ActionEditorView;
}

export const ActionEditorTesterContent: FC<IActionEditorTesterContentProps> = ({ view }) => {
	const [ layoutState ] = useState(() => new LayoutState(ActionEditorTesterLayout));
	const [ paramsModelRef, setParamsModelRef ] = useState(view.getActionModel().getParamsModel());
	const [ paramsModelError, setParamsModelError ] = useState(view.getActionModel().getParamsModelError());
	const [ executionResult, setExecutionResult ] = useState(view.getExecutionResult());

	const { model: paramsModel } = useBlueprintModel(paramsModelRef);

	const executionResultScope = useMemo(() => {
		if (executionResult?.state === ACTION_DELEGATE_STATE.LOADED) {
			return createEmptyScope(
				{
					status: executionResult?.state,
					output: executionResult?.data
				},
				{
					status: Type.String({
						label: "Status"
					}),
					output: executionResult?.type
				}
			);
		} else if (executionResult?.state === ACTION_DELEGATE_STATE.ERROR) {
			return createEmptyScope(
				{
					status: executionResult?.state,
					error: executionResult?.lastError
				},
				{
					status: Type.String({
						label: "Status"
					}),
					error: Type.Any({
						label: "Error"
					})
				}
			);
		} else {
			return createEmptyScope();
		}
	}, [ executionResult ]);

	useSimpleEvent(
		view.getActionModel().onParamsModelBuilt,
		() => {
			setParamsModelRef(view.getActionModel().getParamsModel());
			setParamsModelError(view.getActionModel().getParamsModelError());
		},
		[ view ],
		true
	);

	useSimpleEvent(view.onExecuted, () => setExecutionResult(view.getExecutionResult()), [ view ], true);

	const renderComponent = (componentName: string) => {
		switch (componentName) {
			case "parameters": {
				return (
					<Panel style={PANEL_STYLE.PRIMARY} scroll={true}>
						<FieldSection stateKey="action-tester" collapsible={false} isMain={true}>
							<FieldSectionContent>
								<Inspector model={paramsModel} label="Test Parameters" group>
									{paramsModelError !== undefined && (
										<>
											<PanelNote>
												WARNING: Action parameters have invalid configuration.
											</PanelNote>
										</>
									)}
									{paramsModel &&
										paramsModel.type === SCHEMA_VALUE_TYPE.CONST &&
										Object.keys(paramsModel.constant.props).length === 0 &&
										(
											<PanelNote>No parameters have been configured yet.</PanelNote>
										)
									}
								</Inspector>
							</FieldSectionContent>
						</FieldSection>
					</Panel>
				);
			}
			case "output": {
				return (
					<Panel style={PANEL_STYLE.BASE} scroll={true}>
						{executionResult ? (
							<>
								<ScopeViewer scope={executionResultScope} defaultLevelsExpanded={1} />
							</>
						) : (
							<PanelNote>Run action to display it's output.</PanelNote>
						)}
					</Panel>
				);
			}
			default: {
				return <p>Error: Undefined panel {componentName}.</p>;
			}
		}
	};

	const baseClass = BEM("edt-module-action-editor-tester");

	return (
		<div className={baseClass.element("content").toClassName()}>
			<Layout state={layoutState} renderComponent={renderComponent} />
		</div>
	);
};
